import {Component, OnInit} from '@angular/core';
import {ViewportScroller} from '@angular/common';
import {Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {lastValueFrom} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';
import {Mutex, MutexInterface} from 'async-mutex';

import {DataService} from '../../../Services/data.service';
import {StorageService} from '../../../Services/storage.service';
import {AppConfigService} from '../../../Services/app-config.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {ConfirmDialogService} from '../../../Services/confirm-dialog.service';
import {StripeConnectApService} from '../../../Services/stripe-connect-ap.service';
import {StripePaymentService} from '../../../Services/stripe-payment.service';
import {CreateApTransferRequest} from '../../../DataModels/stripe-connect-ap.models';
import {ToWords} from '../../../Services/ToWordsService/ToWords';
import {PaymentDocumentService} from '../../../Services/payment-document.service';
import {AuthService} from '../../../Services/auth.service';

@Component({
  selector: 'app-issue-payments',
  templateUrl: './issue-payments.component.html',
  styleUrls: ['./issue-payments.component.css'],
  standalone: false
})
export class IssuePaymentsComponent implements OnInit {

  vm: any = {
    list: [],
    check: [],
    stub: [],
    applied: [],
    company: [],
    bank: [],
    checkStock: [],
    entity: [],
    confirmPrint: false,
    printLayout: {},
    item: {header: {}, detail: [{amount: 0}], batch: {type_id: 5}, entity: {}},
    totalPaid: 0,
    subLedger: 'ap',
    type: 'ap',
    searching: false,
    notPrintedOnly: true,
    paymentSearch: '',
    paymentFromDate: '',
    paymentToDate: '',
    showVoidModal: false,
    voidRow: null,
    voidDate: '',
    voidReason: '',
  };

  // ─── Feature state ───────────────────────────────────────────────────────────
  paymentTypeFilter: 'all' | 'check' | 'ach' = 'all';
  paymentStatusFilter: 'all' | 'pending' | 'issued' = 'pending';
  showAchReport = false;
  batchResults: { successCount: number; failCount: number; details: { name: string; status: string; message: string }[] } | null = null;

  isLoaded = false;
  isPaidPreview = false;
  checkAll = false;
  page = 1;
  pageSize = 25;
  totalRecords = 0;
  processingStripe = false;

  // ─── Selection state (updated only on selection events, not getters) ─────────
  selectedRows: any[] = [];
  selectedCheckRows: any[] = [];
  selectedStripeRows: any[] = [];
  achReportTotal = 0;
  private locks: Map<string, MutexInterface>;
  private mutexVar = 'issue-payments-mutex';

  constructor(
    private router: Router,
    private titleService: Title,
    private tableDataService: DataService,
    private localStorage: StorageService,
    private appConfig: AppConfigService,
    private notification: BsToastService,
    private confirmDialogService: ConfirmDialogService,
    private stripeApService: StripeConnectApService,
    private stripePaymentService: StripePaymentService,
    private paymentDocumentService: PaymentDocumentService,
    private vps: ViewportScroller,
    private auth: AuthService
  ) {
    this.locks = new Map();
  }

  ngOnInit() {
    this.titleService.setTitle('Issue Payments');
    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
    this.pageSize = this.appConfig.ListPageSize;

    this.getTableData('bank', '?state=active&pageNumber=0&pageSize=999999', (value) => {
      this.vm.bank = value.Data;
    });
    this.getTableData('company', '?state=active&pageNumber=0&pageSize=999999', (value) => {
      this.vm.company = value.Data;
    });
    this.getTableData('check_stock', '?state=active&pageNumber=0&pageSize=999999', (value) => {
      this.vm.checkStock = value.Data;
    });
    this.getTableData('payment_type', '?state=active&pageNumber=0&pageSize=999999', (value) => {
      this.vm.paymentTypes = value.Data || [];
      this.loadPayments();
    });
  }

  private getTableData(tableName: string, params: string, fun: (v: any) => void) {
    this.tableDataService.getApiListData(tableName, params).subscribe((result: any) => {
      fun(result);
    });
  }

  loadPayments() {
    this.vm.searching = true;
    this.isLoaded = false;
    this.vm.list = [];
    this.checkAll = false;

    let paramString = 'batchType=5&subLedger=ap';
    paramString += '&payment_status=' + this.paymentStatusFilter;
    paramString += '&pageNumber=' + (this.page !== 0 ? this.page - 1 : 0);
    paramString += '&pageSize=' + this.pageSize;
    if (this.vm.paymentSearch) {
      paramString += '&search=' + encodeURIComponent(this.vm.paymentSearch);
    }
    if (this.vm.paymentFromDate) {
      paramString += '&fromDate=' + this.vm.paymentFromDate;
    }
    if (this.vm.paymentToDate) {
      paramString += '&toDate=' + this.vm.paymentToDate;
    }

    this.tableDataService.getApiListData('PaymentList', '?' + paramString).subscribe({
      next: (result: any) => {
        console.log('[IssuePayments][PaymentList] result:', result);
        this.vm.list = result.Data || [];
        this.totalRecords = result.TotalRecords || result.totalRecords || this.vm.list.length || 0;
        this.vm.searching = false;
        this.isLoaded = true;
        this.rebuildSelection();
      },
      error: () => {
        this.vm.searching = false;
        this.isLoaded = true;
      }
    });
  }

  private rebuildSelection() {
    this.selectedRows = (this.vm.list || []).filter((x: any) => x.selected);
    this.selectedCheckRows = this.selectedRows.filter((x: any) => this.isValidForCheck(x) && !this.isStripePayment(x));
    this.selectedStripeRows = this.selectedRows.filter((x: any) => this.isStripePayment(x));
    this.achReportTotal = this.selectedStripeRows.reduce((sum: number, r: any) => sum + Math.abs(Number(r.detail_total || 0)), 0);
  }

  get filteredList(): any[] {
    let list = this.vm.list || [];
    if (this.paymentStatusFilter === 'pending') {
      list = list.filter((x: any) => !x.printed && !x.payment_issued);
    } else if (this.paymentStatusFilter === 'issued') {
      list = list.filter((x: any) => x.printed || x.payment_issued);
    }
    if (this.paymentTypeFilter === 'check') {
      return list.filter((x: any) => this.isManualCheckPayment(x));
    } else if (this.paymentTypeFilter === 'ach') {
      return list.filter((x: any) => this.isStripePayment(x));
    }
    return list;
  }

  isValidPaymentType(row: any): boolean {
    return this.isStripePayment(row) || this.isManualCheckPayment(row);
  }

  private getPaymentType(row: any): any {
    const id = row?.payment_type_id;
    if (!id || !Array.isArray(this.vm.paymentTypes)) {
      return null;
    }
    const normalId = String(id).trim().toLowerCase();
    return this.vm.paymentTypes.find((pt: any) => String(pt.id).trim().toLowerCase() === normalId) || null;
  }

  isManualCheckPayment(row: any): boolean {
    const pt = this.getPaymentType(row);
    if (pt) {
      return !!pt.print_check;
    }
    return !!row?.print_check;
  }

  isValidForCheck(row: any): boolean {
    const pt = this.getPaymentType(row);
    const isPrintCheck = pt ? !!pt.print_check : !!row.print_check;
    return Math.abs(Number(row?.non_credit_memo_applied_total || 0)) > 0
      && !!row?.is_payment_fully_applied
      && !row.printed
      && isPrintCheck;
  }

  isStripePayment(row: any): boolean {
    const pt = this.getPaymentType(row);
    if (pt) {
      const isTruthy = (v: any) => v === true || v === 1 || String(v ?? '').trim().toLowerCase() === 'true';
      if (pt.stripeEnabled !== undefined && pt.stripeEnabled !== null) {
        return isTruthy(pt.stripeEnabled);
      }
      if (pt.stripe_enabled !== undefined && pt.stripe_enabled !== null) {
        return isTruthy(pt.stripe_enabled);
      }
      const name = String(pt.name || '').trim().toLowerCase();
      return name.includes('stripe');
    }
    return !!row?.stripe_enabled || !!row?.stripeEnabled;
  }

  getBankName(row: any): string {
    const bank = (this.vm.bank || []).find((b: any) => b.id === row.bank_id);
    return bank?.name || row.bank_id || '';
  }

  cancelStripePaymentForRow(row: any): void {
    const rowNumber = String(row?.number || '').trim();
    const paymentIntentId = String(row?.stripe_payment_intent_id || '').trim()
      || (/^(pi_|tr_)/i.test(rowNumber) ? rowNumber : '');
    const appEntityId = String(row?.entity_id || '').trim();

    const messages: string[] = [
      `Void / Cancel Stripe Payment #${row.number} for ${row.entity_name}?`,
      `Amount: ${row.detail_total}`,
      'danger: This action cannot be undone. The Stripe payment intent will be permanently canceled and all associated bills will be re-opened.',
    ];

    this.confirmDialogService.confirmThis(messages, () => {
      if (!paymentIntentId) {
        this.notification.showWarningToast('No Stripe payment intent found for this payment.');
        return;
      }
      this.stripePaymentService.cancelPaymentIntent(paymentIntentId, appEntityId).subscribe({
        next: (result: any) => {
          const status = String(result?.status || result?.Status || '').trim().toLowerCase();
          if (status !== 'canceled') {
            this.notification.showWarningToast('Stripe cancellation was not confirmed. Payment was not changed.');
            return;
          }
          this.notification.showSuccessToast('Stripe payment canceled.');
          this.loadPayments();
        },
        error: (error: any) => {
          const msg = error?.error?.Message || error?.error?.message || error?.message || 'Stripe cancellation failed.';
          this.notification.showErrorToast(msg);
        }
      });
    }, () => {
    });
  }

  selectAllRows(checked: boolean) {
    (this.vm.list || []).forEach((item: any) => {
      if (checked && !item.printed && (this.isValidForCheck(item) || this.isStripePayment(item))) {
        item.selected = true;
      } else {
        item.selected = false;
      }
    });
    this.rebuildSelection();
    this.checkAll = checked && this.selectedRows.length > 0;
  }

  selectRow(id: any, checked: boolean) {
    (this.vm.list || []).forEach((item: any) => {
      if (item.id === id) {
        item.selected = checked && !item.printed;
      }
    });
    this.rebuildSelection();
    this.checkAll = (this.vm.list || []).every((x: any) => x.selected);
  }

  navigateTo(id: any) {
    this.router.navigate(['/transactions/payment/form'], {queryParams: {paymentId: id}});
  }

  setPaymentTypeFilter(f: 'all' | 'check' | 'ach') {
    this.paymentTypeFilter = f;
  }

  setPaymentStatusFilter(f: 'all' | 'pending' | 'issued') {
    this.paymentStatusFilter = f;
    this.page = 1;
    this.loadPayments();
  }

  issuePayments(paymentMethod: 'check' | 'ach' | 'none') {
    const ids = this.selectedRows.map((r: any) => r.id);
    if (!ids.length) {
      return;
    }
    this.tableDataService.patchDataByArray('t_header/issue-payments', {ids, paymentMethod}).subscribe({
      next: (res: any) => {
        const updated = res?.updated ?? 0;
        if (updated > 0) {
          this.notification.showSuccessToast(`${updated} payment(s) issued successfully.`);
        } else {
          this.notification.showWarningToast('No pending payments found for the selected IDs.');
        }
        this.loadPayments();
      },
      error: () => this.notification.showErrorToast('Failed to issue payments.')
    });
  }

  unissuePayment(row: any) {
    this.confirmDialogService.confirmThis(['Unissue this payment?'], () => {
      this.tableDataService.putDataByArray('t_header/unissue-payments', {ids: [row.id], paymentMethod: 'none'}).subscribe({
        next: (res: any) => {
          const updated = res?.updated ?? 0;
          if (updated > 0) {
            this.notification.showSuccessToast('Payment unissued successfully.');
          } else {
            this.notification.showWarningToast('Payment cannot be unissued (already printed).');
          }
          this.loadPayments();
        },
        error: () => this.notification.showErrorToast('Failed to unissue payment.')
      });
    }, () => {
    });
  }

  // ─── Physical Check Print ────────────────────────────────────────────────────

  private refreshBanks(): Promise<void> {
    return new Promise((resolve) => {
      this.tableDataService.getApiListData('bank', '?state=active&pageNumber=0&pageSize=999999').subscribe({
        next: (result: any) => {
          this.vm.bank = result.Data;
        },
        error: () => resolve(),
        complete: () => resolve()
      });
    });
  }

  async prepareCheckPrint() {
    await this.refreshBanks();
    if (this.selectedCheckRows.length === 0) {
      this.notification.showWarningToast('No valid payments selected for check printing.');
      return;
    }

    const noAddress = this.selectedCheckRows.filter((x: any) => !x.pay_to_address || x.pay_to_address.length === 0);
    if (noAddress.length > 0) {
      this.notification.showWarningToast('The following vendors have no Pay To address: ' + noAddress.map((x: any) => x.entity_name).join(', '));
      return;
    }

    const uniqueBankIds = [...new Set(this.selectedCheckRows.map((x: any) => x.bank_id))];
    if (uniqueBankIds.length > 1) {
      const firstBankId = this.selectedCheckRows[0].bank_id;
      const firstBank = (this.vm.bank || []).find((b: any) => b.id === firstBankId);
      const firstBankName = firstBank?.name || firstBankId;
      this.notification.showWarningToast(
        `A check run is limited to one bank. Your first selected payment uses "${firstBankName}". Please select only payments from that bank.`
      );
      return;
    }

    if (!this.locks.has(this.mutexVar)) {
      this.locks.set(this.mutexVar, new Mutex());
    }

    this.locks.get(this.mutexVar).acquire().then(async (release) => {
      try {
        this.vm.check = [];
        this.vm.confirmPrint = false;

        const arrayOfItemsToCheck = this.selectedCheckRows.map((x: any) => ({id: x.id, printed: x.printed}));

        this.tableDataService.putDataByArraySilent('t_header/VerifyChecks', arrayOfItemsToCheck).subscribe({
          next: (v: HttpErrorResponse) => {
            if (v !== null && v.status === 418) {
              this.vm.check = [];
              this.vm.confirmPrint = false;
              this.notification.showWarningToast(v.error);
              this.selectAllRows(false);
            }
          },
          error: (e) => {
            console.error(e);
          },
          complete: () => {
            if (!this.vm.confirmPrint) {
              this.selectAllRows(false);
            } else {
              this.vps.scrollToAnchor('checks');
            }
          }
        });

        // For any check missing a number, assign sequentially starting from next_check_number.
        let autoNum: number | null = null;
        if (this.selectedCheckRows.length > 0) {
          const firstBankId = this.selectedCheckRows[0].bank_id;
          const bank = (this.vm.bank || []).find((b: any) => b.id === firstBankId);
          const nextNum = bank?.next_check_number ?? null;
          if (nextNum !== null && nextNum !== undefined) {
            autoNum = parseInt(String(nextNum), 10);
          }
        }

        for (const x of this.selectedCheckRows) {
          this.vm.confirmPrint = true;
          this.vm.printLayout = {};
          if (autoNum !== null) {
            x.number = String(autoNum++);
          }

          const tempCompany = this.filterObject(this.vm.company, {id: x.company_id}, true)[0];
          const tempBank = this.filterObject(this.vm.bank, {id: x.bank_id}, true)[0];

          if (!tempCompany || !tempBank) {
            this.notification.showWarningToast('Company or bank data not loaded yet. Please try again.');
            this.vm.confirmPrint = false;
            break;
          }

          this.vm.stub = [{id: 1}, {id: 2}];
          this.vm.printLayout.checkStock = this.filterObject(this.vm.checkStock, {id: tempBank.stock_id}, true)[0];

          let tempCompanyAddress2 = '';
          let tempBankAddress2 = '';

          if (tempCompany.city && (tempCompany.state || tempCompany.zip)) {
            tempCompanyAddress2 = tempCompany.city + ', ' + tempCompany.state + ' ' + tempCompany.zip;
          } else {
            tempCompanyAddress2 = (tempCompany.state || '') + ' ' + (tempCompany.zip || '');
          }
          if (tempBank.city && (tempBank.state || tempBank.zip)) {
            tempBankAddress2 = tempBank.city + ', ' + tempBank.state + ' ' + tempBank.zip;
          } else {
            tempBankAddress2 = (tempBank.state || '') + ' ' + (tempBank.zip || '');
          }

          tempCompany.logo = '';

          const words = new ToWords();
          const amountInWords = words.convert(x.detail_total * -1, {currency: true});

          x.companyName = tempCompany.name;
          x.companyDBA = tempCompany.dba;
          x.companyAddress1 = tempCompany.address_1;
          x.companyAddress2 = tempCompanyAddress2;
          x.companyPhone = tempCompany.phone;
          x.bankName = tempBank.check_name;
          x.bankAddress1 = tempBank.address_1;
          x.bankAddress2 = tempBankAddress2;
          x.bankPhone = tempBank.phone;
          x.payeeName = x.entity_name;
          x.payeeAddress1 = x.address_1;
          x.payeeAddress2 = x.address_2;
          x.date = x.header_date;
          x.accountNumber = tempBank.account_number;
          x.transitNumber = tempBank.transit_number;
          x.institutionNumber = tempBank.institution_number;
          x.routingNumber = tempBank.routing_number || '';
          x.us = tempBank.us || false;
          x.amountInWords = amountInWords;
          x.applied = Array.isArray(x.applied) ? x.applied : [];
          const xNonCreditTotal = Math.abs(Number(x.non_credit_memo_applied_total || 0));
          x.amount = x.payment_applied_total * -1;
          x.paymentAmount = xNonCreditTotal;
          x.totalPaid = xNonCreditTotal;

          this.vm.check.push(x);
        }

        // Build vm.item for payment-print stub rendering (uses first check's data)
        if (this.vm.check.length > 0) {
          const first = this.vm.check[0];
          this.vm.item = {
            header: {number: first.number, header_date: first.header_date, entity_id: first.entity_id},
            detail: [{amount: first.totalPaid}],
            batch: {type_id: 5},
            entity: {}
          };
          this.vm.applied = first.applied || [];
          this.vm.totalPaid = first.totalPaid;
          this.vm = {...this.vm};
        }

      } finally {
        release();
      }
    });
  }


  confirmPostChecks() {
    this.updateHeaderPrinted();
  }

  previewChecksOnly() {
    this.isPaidPreview = false;
    this.prepareCheckPrint();
  }

  async previewIssuedCheck(row: any): Promise<void> {
    await this.refreshBanks();
    const tempCompany = this.filterObject(this.vm.company, {id: row.company_id}, true)[0];
    const tempBank = this.filterObject(this.vm.bank, {id: row.bank_id}, true)[0];

    if (!tempCompany || !tempBank) {
      this.notification.showWarningToast('Company or bank data not loaded yet. Please try again.');
      return;
    }

    let tempCompanyAddress2 = '';
    let tempBankAddress2 = '';
    if (tempCompany.city && (tempCompany.state || tempCompany.zip)) {
      tempCompanyAddress2 = tempCompany.city + ', ' + tempCompany.state + ' ' + tempCompany.zip;
    } else {
      tempCompanyAddress2 = (tempCompany.state || '') + ' ' + (tempCompany.zip || '');
    }
    if (tempBank.city && (tempBank.state || tempBank.zip)) {
      tempBankAddress2 = tempBank.city + ', ' + tempBank.state + ' ' + tempBank.zip;
    } else {
      tempBankAddress2 = (tempBank.state || '') + ' ' + (tempBank.zip || '');
    }

    const words = new ToWords();
    const amountInWords = words.convert(Math.abs(Number(row.detail_total || 0)), {currency: true});

    const check = {...row};
    check.companyName = tempCompany.name;
    check.companyDBA = tempCompany.dba;
    check.companyAddress1 = tempCompany.address_1;
    check.companyAddress2 = tempCompanyAddress2;
    check.companyPhone = tempCompany.phone;
    check.bankName = tempBank.check_name;
    check.bankAddress1 = tempBank.address_1;
    check.bankAddress2 = tempBankAddress2;
    check.bankPhone = tempBank.phone;
    check.payeeName = row.entity_name;
    check.payeeAddress1 = row.address_1;
    check.payeeAddress2 = row.address_2;
    check.date = row.header_date;
    check.accountNumber = tempBank.account_number;
    check.transitNumber = tempBank.transit_number;
    check.institutionNumber = tempBank.institution_number;
    check.routingNumber = tempBank.routing_number || '';
    check.us = tempBank.us || false;
    check.amountInWords = amountInWords;
    check.applied = Array.isArray(row.applied) ? row.applied : [];
    const nonCreditTotal = Math.abs(Number(row.non_credit_memo_applied_total || 0));
    check.amount = Math.abs(Number(row.payment_applied_total || 0));
    check.paymentAmount = nonCreditTotal;
    check.totalPaid = nonCreditTotal;

    this.vm.check = [check];
    this.vm.stub = [{id: 1}, {id: 2}];
    this.vm.printLayout = {};
    this.vm.printLayout.checkStock = this.filterObject(this.vm.checkStock, {id: tempBank.stock_id}, true)[0];
    this.vm.item = {
      header: {number: check.number, header_date: check.header_date, entity_id: check.entity_id},
      detail: [{amount: check.totalPaid}],
      batch: {type_id: 5},
      entity: {}
    };
    this.vm.applied = check.applied;
    this.vm.totalPaid = check.totalPaid;
    this.vm = {...this.vm};
    this.vm.confirmPrint = true;
    this.isPaidPreview = true;
    this.vps.scrollToAnchor('checks');
  }

  updateHeaderPrinted() {
    this.vm.confirmPrint = false;
    const issuedBy = this.auth.getAppUserInfo().userId;
    const issuedDate = new Date().toISOString();
    const arrayOfItemsToChange = this.selectedCheckRows.map((x: any) => ({
      id: x.id,
      printed: true,
      number: x.number ?? null,
      payment_issued: true,
      payment_issued_by: issuedBy,
      payment_issued_date: issuedDate
    }));

    console.log('[Check Issue] VerifyChecks payload:', arrayOfItemsToChange);
    this.tableDataService.putDataByArraySilent('t_header/VerifyChecks', arrayOfItemsToChange).subscribe((v: HttpErrorResponse) => {
      if (v != null && v.status === 418) {
        this.vm.check = [];
        this.vm.confirmPrint = false;
        this.notification.showWarningToast(v.error);
        this.selectAllRows(false);
      } else {
        // Build self-contained check HTML and print via service iframe
        const checkHtml = this.paymentDocumentService.buildApCheckHtml(this.vm);
        this.paymentDocumentService.printHtml(checkHtml).then(() => {
          this.confirmDialogService.confirmThis(
            ['Did the checks print correctly?', 'Confirming will mark them as printed and finalize the batch.'],
            () => {
              // User confirmed — finalize
              console.log('[Check Issue] Finalize t_header payload:', arrayOfItemsToChange);
              this.tableDataService.putDataByArraySilent('t_header', arrayOfItemsToChange).subscribe(() => {
                this.vm.check = [];
                this.vm.confirmPrint = false;
                this.checkAll = false;
                this.loadPayments();
              });
            },
            () => {
              // User said checks did not print correctly — reverse the VerifyChecks lock and stay on page
              const reverseItems = arrayOfItemsToChange.map((x: any) => ({id: x.id, printed: false}));
              console.log('[Check Issue] Reverse VerifyChecks payload:', reverseItems);
              this.tableDataService.putDataByArraySilent('t_header/VerifyChecks', reverseItems).subscribe();
              this.notification.showWarningToast('Check print was not confirmed. Checks remain unprinted — please reprint.');
            }
          );
        });
      }
    });
  }

  // ─── Stripe ACH ──────────────────────────────────────────────────────────────

  toggleAchReport() {
    this.showAchReport = !this.showAchReport;
  }

  confirmStripeAch() {
    if (this.selectedStripeRows.length === 0) {
      this.notification.showWarningToast('No Stripe/ACH payments selected.');
      return;
    }
    const messages: string[] = [
      `Send ${this.selectedStripeRows.length} ACH payment(s) via Stripe?`,
      'This will initiate an ACH transfer to vendor bank account(s).',
      '*Note - this action is unlikely to be reversible.'
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.processStripeAch();
      },
      () => {
        this.notification.showSuccessToast('ACH Canceled');
      }
    );
  }

  async processStripeAch() {
    this.processingStripe = true;
    let successCount = 0;
    let failCount = 0;

    // Verify no checks have already been issued (same concurrency guard as physical checks)
    const verifyPayload = this.selectedStripeRows.map((x: any) => ({id: x.id, printed: true}));
    const verifyResult: any = await lastValueFrom(
      this.tableDataService.putDataByArraySilent('t_header/VerifyChecks', verifyPayload)
    );
    if (verifyResult != null && verifyResult.status === 418) {
      this.processingStripe = false;
      this.notification.showWarningToast(verifyResult.error || 'One or more payments have already been issued. Please refresh and try again.');
      this.selectAllRows(false);
      this.loadPayments();
      return;
    }

    for (const row of this.selectedStripeRows) {
      try {
        const transferReq: CreateApTransferRequest = {
          appEntityId: String(row.entity_id || ''),
          appBankId: String(row.bank_id || ''),
          companyId: String(row.company_id || ''),
          locationId: String(row.location_id || ''),
          appTransactionId: String(row.number || row.id || ''),
          amount: Math.abs(Number(row.detail_total || 0)),
          currency: 'usd',
          referenceId: String(row.reference || row.doc_no || ''),
          description: `Vendor payment for ${String(row.entity_name || 'AP Payment').trim()}`,
        };

        const transfer = await lastValueFrom(this.stripeApService.createTransfer(transferReq));

        if (transfer?.isPayoutReady === false) {
          this.notification.showWarningToast(`${row.entity_name}: ${transfer.message || 'Vendor not ready for Stripe payouts.'}`);
          failCount++;
          continue;
        }

        // Mark as printed/issued so it leaves the unissued list
        const changes = [{id: row.id, printed: true}];
        await lastValueFrom(this.tableDataService.putDataByArraySilent('t_header', changes));
        successCount++;
      } catch (e) {
        this.notification.showErrorToast(`ACH failed for ${row.entity_name || row.number}: Please check Stripe setup.`);
        failCount++;
      }
    }

    this.processingStripe = false;
    this.showAchReport = false;
    this.batchResults = {
      successCount,
      failCount,
      details: (this.selectedStripeRows as any[]).map((row: any, i: number) => ({
        name: row.entity_name || row.number,
        status: i < successCount ? 'success' : 'failed',
        message: i < successCount ? 'Transfer initiated' : 'Failed'
      }))
    };
    if (successCount > 0) {
      this.notification.showSuccessToast(`${successCount} ACH transfer(s) initiated successfully.`);
    }
    if (failCount > 0) {
      this.notification.showWarningToast(`${failCount} ACH transfer(s) failed. Check Stripe setup for those vendors.`);
    }
    this.loadPayments();
  }

  dismissBatchResults() {
    this.batchResults = null;
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  filterObject(data: any[], filterObj: any, exactMatch: boolean): any[] {
    if (!data) {
      return [];
    }
    return data.filter((item: any) => {
      return Object.keys(filterObj).every(key => {
        if (exactMatch) {
          return item[key] == filterObj[key];
        }
        return String(item[key] || '').toLowerCase().includes(String(filterObj[key] || '').toLowerCase());
      });
    });
  }

  clearPreview() {
    this.vm.check = [];
    this.vm.confirmPrint = false;
    this.isPaidPreview = false;
    this.selectAllRows(false);
    this.vps.scrollToAnchor('top');
  }

  onNotPrintedOnlyChange() {
    this.page = 1;
    this.loadPayments();
  }

  serverSearch(value: any) {
    this.vm.paymentSearch = value.searchTerm;
    this.vm.paymentFromDate = value.fromDate;
    this.vm.paymentToDate = value.toDate;
    this.page = 1;
    this.loadPayments();
  }

  voidPayment(row: any) {
    if (!row?.printed) {
      this.notification.showWarningToast('Only printed checks can be voided.');
      return;
    }
    this.confirmDialogService.confirmThis(
      [
        `Void Check #${row.number} for ${row.entity_name}?`,
        `Amount: ${row.detail_total}`,
        'danger: This action cannot be undone. The check will be permanently voided and all associated bills will be re-opened.',
      ],
      () => {
        this.vm.voidRow = row;
        this.vm.showVoidModal = true;
        this.vm.voidDate = new Date().toISOString().substring(0, 10);
        this.vm.voidReason = '';
      },
      () => {
      }
    );
  }

  cancelVoid() {
    this.vm.showVoidModal = false;
    this.vm.voidRow = null;
    this.vm.voidDate = '';
    this.vm.voidReason = '';
  }

  confirmVoid() {
    if (!this.vm.voidDate || !this.vm.voidReason?.trim()) {
      this.notification.showWarningToast('Void Date and Reason are both required.');
      return;
    }
    const row = this.vm.voidRow;
    const endpoint = `t_header/${row.id}/void`;
    const payload = {voidDate: this.vm.voidDate, reason: this.vm.voidReason.trim()};
    console.log('[Void Payment] POST', endpoint, payload);
    this.tableDataService.post(endpoint, payload).subscribe({
      next: (result: any) => {
        console.log('[Void Payment] result', result);
        this.vm.showVoidModal = false;
        this.notification.showSuccessToast(`Check #${row.number} has been voided. The associated bill(s) have been re-opened.`);
        this.loadPayments();
      },
      error: (err: any) => {
        const msg = err?.error?.message || err?.error?.Message || '';
        if (err?.status === 400 && msg.toLowerCase().includes('reconcil')) {
          this.notification.showErrorToast('This payment cannot be voided — one or more lines have been included in a completed bank reconciliation.');
        } else if (err?.status === 400) {
          this.notification.showErrorToast(msg || 'Void failed. Please check the payment and try again.');
        } else {
          this.notification.showErrorToast('Void failed. Please try again.');
        }
      }
    });
  }

  clearServerSearch(value: any) {
    this.vm.paymentSearch = value.searchTerm;
    this.vm.paymentFromDate = value.fromDate;
    this.vm.paymentToDate = value.toDate;
    this.page = 1;
    this.loadPayments();
  }
}
